import { useState } from 'react'
import { achsenBereich, reihenFarbe, useBreite } from './diagramm'
import { Hinweis } from './Hinweis'

export interface Reihe {
  schluessel: string
  name: string
  werte: (number | null)[]
  gestrichelt?: boolean
  duenn?: boolean
}

/**
 * Mehrere Reihen auf einer Achse.
 *
 * Ausdrücklich EINE Achse. Zwei Größen verschiedener Größenordnung
 * gehören nicht in ein Bild mit zwei Skalen — dabei lässt sich jede
 * gewünschte Aussage erzeugen, indem man die Skalen verschiebt. Wo zwei
 * Einheiten nötig wären, stehen hier zwei Diagramme.
 *
 * Die drei Sichten auf eine Quote — je Tag, gleitend, auflaufend — sind
 * derselbe Prozentwert und gehören deshalb zusammen.
 */
export function MehrfachLinie({
  etiketten, reihen, hoehe = 260, formatiere, formatiereAchse, einheit = '',
  abNull = false,
}: {
  etiketten: string[]
  reihen: Reihe[]
  hoehe?: number
  formatiere: (w: number) => string
  formatiereAchse: (w: number) => string
  einheit?: string
  /** Quoten brauchen das nicht — siehe achsenBereich. Beträge schon. */
  abNull?: boolean
}) {
  const [ref, breite] = useBreite<HTMLDivElement>()
  const [aktiv, setAktiv] = useState<number | null>(null)
  const [lage, setLage] = useState({ x: 0, y: 0 })

  const links = 52, rechts = 14, oben = 16, unten = 26
  const zb = Math.max(0, breite - links - rechts)
  const zh = hoehe - oben - unten
  const alle = reihen.flatMap((r) => r.werte).filter((v): v is number => v !== null)
  const max = alle.length ? Math.max(...alle) : 1
  const min = alle.length ? Math.min(...alle) : 0
  const { schritte, untergrenze, obergrenze } = achsenBereich(abNull ? 0 : min, max)
  const spanne = obergrenze - untergrenze || 1
  const y = (v: number) => oben + zh * (1 - (v - untergrenze) / spanne)
  const n = etiketten.length
  const x = (i: number) => n <= 1 ? links + zb / 2 : links + (zb * i) / (n - 1)

  const pfad = (werte: (number | null)[]) => {
    let d = '', offen = false
    werte.forEach((v, i) => {
      if (v === null) { offen = false; return }
      d += `${offen ? 'L' : 'M'}${x(i)} ${y(v)} `
      offen = true
    })
    return d.trim()
  }

  const beiBewegung = (e: React.MouseEvent<SVGRectElement>) => {
    const k = e.currentTarget.getBoundingClientRect()
    const i = n <= 1 ? 0 : Math.round(((e.clientX - k.left) / (zb || 1)) * (n - 1))
    setAktiv(Math.max(0, Math.min(n - 1, i)))
    setLage({ x: e.clientX, y: e.clientY })
  }

  const wichtig = new Set<number>([0, n - 1])

  return (
    <div ref={ref}>
      {breite > 0 && n > 0 && (
        <svg className="diagramm" height={hoehe} role="img"
             aria-label={`Verlauf von ${reihen.length} Reihen über ${n} Tage`}>
          {schritte.map((s) => (
            <g key={s}>
              <line className={s === untergrenze ? 'grundlinie' : 'gitterlinie'}
                    x1={links} x2={links + zb} y1={y(s)} y2={y(s)} />
              <text className="achsentext" x={links - 8} y={y(s) + 3}
                    textAnchor="end">{formatiereAchse(s)}</text>
            </g>
          ))}

          {reihen.map((r, ri) => (
            <path key={r.schluessel} d={pfad(r.werte)}
                  className="marke-linie"
                  stroke={reihenFarbe(ri)}
                  strokeWidth={r.duenn ? 1.25 : 2}
                  strokeOpacity={r.duenn ? 0.55 : 1}
                  strokeDasharray={r.gestrichelt ? '5 4' : undefined} />
          ))}

          {etiketten.map((e, i) => wichtig.has(i) && (
            <text key={'e' + i} className="achsentext" x={x(i)} y={hoehe - 9}
                  textAnchor={i === 0 ? 'start' : 'end'}>{e}</text>
          ))}

          {aktiv !== null && (
            <g>
              <line className="gitterlinie" x1={x(aktiv)} x2={x(aktiv)}
                    y1={oben} y2={y(untergrenze)} style={{ stroke: 'var(--linie-2)' }} />
              {reihen.map((r, ri) => r.werte[aktiv] !== null && (
                <circle key={r.schluessel} cx={x(aktiv)} cy={y(r.werte[aktiv]!)}
                        r={4.5} fill={reihenFarbe(ri)} className="marke-trenner" />
              ))}
            </g>
          )}

          <rect x={links} y={oben} width={zb} height={zh} fill="transparent"
                onMouseMove={beiBewegung} onMouseLeave={() => setAktiv(null)} />
        </svg>
      )}

      <div className="legende" style={{ marginTop: 10 }}>
        {reihen.map((r, ri) => (
          <span className="eintrag" key={r.schluessel}>
            <span className="klecks" style={{
              background: reihenFarbe(ri),
              opacity: r.duenn ? 0.55 : 1,
              height: r.gestrichelt ? 3 : 9,
            }} />
            {r.name}
          </span>
        ))}
      </div>

      {aktiv !== null && (
        <Hinweis lage={lage}>
          <div className="titel">{etiketten[aktiv]}</div>
          {reihen.map((r, ri) => (
            <div className="zeile" key={r.schluessel}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span className="klecks" style={{
                  background: reihenFarbe(ri), width: 8, height: 8, borderRadius: 2,
                }} />
                <span className="dezent">{r.name}</span>
              </span>
              <span>{r.werte[aktiv] === null ? '–'
                : formatiere(r.werte[aktiv]!) + einheit}</span>
            </div>
          ))}
        </Hinweis>
      )}
    </div>
  )
}
